import { ICON_SELECTOR } from '@/enum/Selectors';

/**
 * CSS selector for elements that should have font size adjusted
 */
const FONT_SIZE_SELECTOR = 'h1,h2,h3,h4,h5,h6,p,a,dl,dt,li,ol,th,td,span,blockquote,.cya11y-text,div,button';

/**
 * Set of icon selectors to exclude from font size adjustments
 */
const ICON_SELECTOR_SET = new Set(ICON_SELECTOR);

/**
 * Data attribute key for storing original font size
 */
const ORIGINAL_FONT_SIZE_ATTR = 'cya11yOrgFontSize';

/**
 * Data attribute key for storing original inline font size (if element had one before widget)
 */
const ORIGINAL_INLINE_FONT_SIZE_ATTR = 'cya11yOrgInlineFontSize';

/**
 * Adjusts font size of text elements on the page
 * @param multiply - Font size multiplier (default: 1)
 * @returns void
 */
export default function adjustFontSize(multiply: number = 1): void {
  try {
    if (typeof multiply !== 'number' || multiply <= 0) {
      console.warn('Invalid font size multiplier:', multiply);
      return;
    }

    const elements = document.querySelectorAll<HTMLElement>(FONT_SIZE_SELECTOR);

    // Pass 1: Snapshot every matching element's original computed font-size BEFORE
    // modifying any element. Doing capture and apply in the same loop is unsafe:
    // when a parent's inline font-size is set first, descendants reading
    // getComputedStyle().fontSize see the parent's MODIFIED value (via inheritance)
    // and store an inflated "original" — compounding ×multiply on every nested
    // level on every page load.
    elements.forEach((element: HTMLElement) => {
      try {
        // Skip elements that contain any class in ICON_SELECTOR_SET
        if (Array.from(element.classList).some(cls => ICON_SELECTOR_SET.has(cls))) {
          return;
        }

        // Only capture once — preserves the very first reading across menu
        // re-renders inside the same page lifetime.
        if (element.dataset[ORIGINAL_FONT_SIZE_ATTR]) {
          return;
        }

        const originalInlineStyle = element.style.fontSize;
        const computedFontSize = parseInt(window.getComputedStyle(element).fontSize);

        if (isNaN(computedFontSize) || computedFontSize <= 0) {
          console.warn('Invalid original font size for element:', element);
          return;
        }

        element.dataset[ORIGINAL_FONT_SIZE_ATTR] = String(computedFontSize);
        if (originalInlineStyle) {
          element.dataset[ORIGINAL_INLINE_FONT_SIZE_ATTR] = originalInlineStyle;
        }
      } catch (captureError) {
        console.warn('Failed to capture original font size for element:', element, captureError);
      }
    });

    // Pass 2: Apply the multiplier (or restore on multiply === 1) using the
    // datasets captured in pass 1.
    elements.forEach((element: HTMLElement) => {
      try {
        if (Array.from(element.classList).some(cls => ICON_SELECTOR_SET.has(cls))) {
          return;
        }

        const originalFontSize = Number(element.dataset[ORIGINAL_FONT_SIZE_ATTR]);
        if (isNaN(originalFontSize) || originalFontSize <= 0) {
          // Either capture failed for this element, or the selector matched
          // something we can't reason about. Skip silently — pass 1 already
          // logged anything noteworthy.
          return;
        }

        if (multiply === 1) {
          // Restore original state. Only meaningful if widget had previously
          // applied an inline font-size (i.e. this element has dataset).
          if (element.dataset[ORIGINAL_INLINE_FONT_SIZE_ATTR]) {
            element.style.fontSize = element.dataset[ORIGINAL_INLINE_FONT_SIZE_ATTR];
          } else {
            element.style.fontSize = '';
          }
          return;
        }

        const newFontSize = originalFontSize * multiply;
        element.style.fontSize = `${newFontSize.toFixed(0)}px`;
      } catch (applyError) {
        console.warn('Failed to apply font size for element:', element, applyError);
      }
    });
  } catch (error) {
    console.error('Failed to adjust font size:', error);
  }
}